import { inviteEventMember } from "@/server/invitations.functions";
import {
  conectarWhatsappFn,
  configuracionWhatsapp,
  reintentarConexionWhatsapp,
  type ConfiguracionWhatsapp,
} from "@/server/whatsapp.functions";
import type { ResultadoConexion } from "@/whatsapp/conexion";
import { sincronizarMetaAhora, type ResumenSincronizacion } from "@/server/meta.functions";
import {
  normalizarFilaCampana,
  type FilaCampana,
  type RangoFechas,
  type UltimaSincronizacion,
} from "@/domain/campanas";
import { BUCKET_GRABACIONES, rutaGrabacion, validarAudio } from "@/domain/grabaciones";
import { enTandas, type MensajeAImportar } from "@/domain/whatsappExportado";
import { normalizeSongValue } from "@/domain/songs";
import { supabase } from "@/integrations/supabase/client";
import type {
  ActivityItem,
  AgendaItem,
  Client,
  EventFile,
  EventRecord,
  EventStatus,
  EventType,
  FileCategory,
  GuestEvent,
  GuestSong,
  InteractionKind,
  InteractionOutcome,
  Lead,
  LeadIndagacion,
  LeadInteraction,
  LeadProposal,
  LeadProposalEvent,
  LeadStage,
  LlamadaPendiente,
  ModerationStatus,
  MusicPreference,
  Note,
  Payment,
  PaymentStatus,
  Quote,
  QuoteStatus,
  Song,
  SongCategory,
  SpecialMoment,
  Task,
  TaskStatus,
  Viewer,
  WeddingSection,
  WeddingSectionType,
  WhatsappEstado,
  WhatsappMensaje,
} from "@/domain/types";

type DataError = { message: string } | null;

function ensure(error: DataError): void {
  if (error) throw new Error(error.message);
}

function rows<T>(data: unknown): T[] {
  return (data ?? []) as T[];
}

function row<T>(data: unknown): T | null {
  return (data ?? null) as T | null;
}

export const authRepository = {
  async getViewer(): Promise<Viewer | null> {
    const { data, error } = await supabase.auth.getUser();
    if (error || !data.user) return null;
    await supabase.rpc("accept_portal_invitations");
    const [{ data: profile }, { data: role }, { data: membership }] = await Promise.all([
      supabase.from("profiles").select("display_name").eq("id", data.user.id).maybeSingle(),
      supabase.from("user_roles").select("role").eq("user_id", data.user.id).maybeSingle(),
      supabase
        .from("event_members")
        .select("event_id")
        .eq("user_id", data.user.id)
        .limit(1)
        .maybeSingle(),
    ]);
    return {
      id: data.user.id,
      email: data.user.email ?? "",
      displayName:
        (profile as { display_name?: string } | null)?.display_name ||
        data.user.email?.split("@")[0] ||
        "SUSIEDAD",
      isAdmin: Boolean(role),
      hasPortal: Boolean(membership),
    };
  },
  async signIn(email: string, password: string): Promise<Viewer> {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    ensure(error);
    const viewer = await this.getViewer();
    if (!viewer) throw new Error("No se pudo cargar el perfil");
    return viewer;
  },
  async signOut(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    ensure(error);
  },
  async requestPasswordReset(email: string): Promise<void> {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/acceso?mode=reset`,
    });
    ensure(error);
  },
  async updatePassword(password: string): Promise<void> {
    const { error } = await supabase.auth.updateUser({ password });
    ensure(error);
  },
};

const LEAD_COLUMNS = "*";

export interface RegistrarInteraccion {
  leadId: string;
  kind: InteractionKind;
  outcome: InteractionOutcome | null;
  body?: string | null;
  meetingAt?: string | null;
  snoozeUntil?: string | null;
  lostReason?: string | null;
}

export interface NuevoLead {
  name: string;
  phone?: string | null;
  email?: string | null;
  eventDate?: string | null;
  location?: string | null;
  note?: string | null;
  /** "manual" (por defecto) o "presentacion" si nace al cerrar una presentación. */
  source?: "manual" | "presentacion";
}

/** Datos de `crm_reservar`: el paso de lead a cliente. */
export interface ReservaLead {
  leadId: string;
  titulo?: string | null;
  startsAt?: string | null;
  venue?: string | null;
  services?: string[] | null;
  /** `null` = a medida: no se crea presupuesto. */
  totalCents: number | null;
  /** Por defecto, la señal de 200 €. */
  senalCents?: number;
  senalPagada?: boolean;
  metodo?: string | null;
  indagacion?: LeadIndagacion | null;
  /** Cliente y boda ya creados (la presentación), para reutilizarlos. */
  clientId?: string | null;
  eventId?: string | null;
}

export interface ResultadoReserva {
  clientId: string;
  eventId: string;
  quoteId: string | null;
  paymentId: string | null;
}

/** Leads de venta: las solicitudes de contacto con su etapa e interacciones. */
export const leadRepository = {
  async list(): Promise<Lead[]> {
    const { data, error } = await supabase
      .from("contact_requests")
      .select(LEAD_COLUMNS)
      .order("created_at", { ascending: false });
    ensure(error);
    return rows<Lead>(data);
  },
  async get(id: string): Promise<Lead | null> {
    const { data, error } = await supabase
      .from("contact_requests")
      .select(LEAD_COLUMNS)
      .eq("id", id)
      .maybeSingle();
    ensure(error);
    return row<Lead>(data);
  },
  async interactions(id: string): Promise<LeadInteraction[]> {
    const { data, error } = await supabase
      .from("lead_interactions")
      .select("*")
      .eq("request_id", id)
      .order("created_at", { ascending: false });
    ensure(error);
    return rows<LeadInteraction>(data);
  },
  /** Última interacción de cada lead, para las tarjetas del tablero. */
  async lastInteractions(): Promise<Map<string, LeadInteraction>> {
    const { data, error } = await supabase
      .from("lead_interactions")
      .select("*")
      .neq("kind", "NOTE")
      .order("created_at", { ascending: false })
      .limit(1000);
    ensure(error);
    const ultimas = new Map<string, LeadInteraction>();
    for (const interaccion of rows<LeadInteraction>(data)) {
      if (!ultimas.has(interaccion.request_id)) ultimas.set(interaccion.request_id, interaccion);
    }
    return ultimas;
  },
  async registrar(input: RegistrarInteraccion): Promise<LeadInteraction> {
    const { data, error } = await supabase.rpc("crm_registrar_interaccion", {
      p_request_id: input.leadId,
      p_kind: input.kind,
      p_outcome: input.outcome,
      p_body: input.body?.trim() || null,
      p_meeting_at: input.meetingAt || null,
      p_snooze_until: input.snoozeUntil || null,
      p_lost_reason: input.lostReason?.trim() || null,
    });
    ensure(error);
    return row<LeadInteraction>(data) as LeadInteraction;
  },
  async moverEtapa(id: string, stage: LeadStage, lostReason?: string | null): Promise<void> {
    const { error } = await supabase.rpc("crm_mover_etapa", {
      p_request_id: id,
      p_stage: stage,
      p_lost_reason: lostReason?.trim() || null,
    });
    ensure(error);
  },
  async agenda(now?: Date): Promise<AgendaItem[]> {
    const { data, error } = await supabase.rpc(
      "crm_agenda",
      now ? { p_now: now.toISOString() } : {},
    );
    ensure(error);
    return rows<AgendaItem>(data);
  },
  posponer(id: string, until: string, body?: string | null): Promise<LeadInteraction> {
    return this.registrar({
      leadId: id,
      kind: "NOTE",
      outcome: "FOLLOW_UP",
      body,
      snoozeUntil: until,
    });
  },
  /** Alta a mano por el administrador (lead de una llamada, una feria…). */
  async crear(input: NuevoLead): Promise<Lead> {
    const { data, error } = await supabase
      .from("contact_requests")
      .insert({
        name: input.name.trim(),
        phone: input.phone?.trim() || null,
        email: input.email?.trim().toLowerCase() || null,
        event_type: "WEDDING",
        event_date: input.eventDate || null,
        location: input.location?.trim() || null,
        message: input.note?.trim() || null,
        source: input.source ?? "manual",
        consent_at: new Date().toISOString(),
      })
      .select(LEAD_COLUMNS)
      .single();
    ensure(error);
    return row<Lead>(data) as Lead;
  },
  /** El lead que se convirtió en este cliente, si lo hay. */
  async deCliente(clientId: string): Promise<Lead | null> {
    const { data, error } = await supabase
      .from("contact_requests")
      .select(LEAD_COLUMNS)
      .eq("converted_client_id", clientId)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    ensure(error);
    return row<Lead>(data);
  },
  /** Reserva: crea cliente, boda, presupuesto aceptado y señal. Idempotente. */
  async reservar(input: ReservaLead): Promise<ResultadoReserva> {
    const { data, error } = await supabase.rpc("crm_reservar", {
      p_request_id: input.leadId,
      p_titulo: input.titulo?.trim() || null,
      p_starts_at: input.startsAt || null,
      p_venue: input.venue?.trim() || null,
      p_services: input.services ?? null,
      p_total_cents: input.totalCents,
      ...(input.senalCents === undefined ? {} : { p_senal_cents: input.senalCents }),
      p_senal_pagada: input.senalPagada ?? false,
      p_metodo: input.metodo?.trim() || null,
      p_indagacion: input.indagacion ?? null,
      p_client_id: input.clientId || null,
      p_event_id: input.eventId || null,
    });
    ensure(error);
    const fila = rows<{
      client_id: string;
      event_id: string;
      quote_id: string | null;
      payment_id: string | null;
    }>(data)[0];
    if (!fila) throw new Error("No se pudo reservar");
    return {
      clientId: fila.client_id,
      eventId: fila.event_id,
      quoteId: fila.quote_id,
      paymentId: fila.payment_id,
    };
  },
};

export const clientRepository = {
  async get(id: string): Promise<Client | null> {
    const { data, error } = await supabase.from("clients").select("*").eq("id", id).maybeSingle();
    ensure(error);
    return row<Client>(data);
  },
  async list(includeArchived = false): Promise<Client[]> {
    let query = supabase.from("clients").select("*").order("created_at", { ascending: false });
    if (!includeArchived) query = query.is("archived_at", null);
    const { data, error } = await query;
    ensure(error);
    return rows<Client>(data);
  },
  async save(input: Partial<Client> & Pick<Client, "first_name">): Promise<Client> {
    const payload = {
      first_name: input.first_name.trim(),
      last_name: input.last_name?.trim() ?? "",
      company_name: input.company_name || null,
      email: input.email?.toLowerCase() || null,
      phone: input.phone || null,
      address: input.address || null,
      tax_id: input.tax_id || null,
      preferred_contact: input.preferred_contact || null,
      notes: input.notes || null,
    };
    const request = input.id
      ? supabase.from("clients").update(payload).eq("id", input.id).select().single()
      : supabase.from("clients").insert(payload).select().single();
    const { data, error } = await request;
    ensure(error);
    return row<Client>(data) as Client;
  },
  async archive(id: string): Promise<void> {
    const { error } = await supabase
      .from("clients")
      .update({ archived_at: new Date().toISOString() })
      .eq("id", id);
    ensure(error);
  },
};

export const eventRepository = {
  async list(): Promise<EventRecord[]> {
    const { data, error } = await supabase
      .from("events")
      .select("*, clients(first_name,last_name,email,phone)")
      .order("starts_at", { ascending: true, nullsFirst: false });
    ensure(error);
    return rows<EventRecord>(data);
  },
  async ofClient(clientId: string): Promise<EventRecord[]> {
    const { data, error } = await supabase
      .from("events")
      .select("*, clients(first_name,last_name,email,phone)")
      .eq("client_id", clientId)
      .order("starts_at", { ascending: true, nullsFirst: false });
    ensure(error);
    return rows<EventRecord>(data);
  },
  async get(id: string): Promise<EventRecord | null> {
    const { data, error } = await supabase
      .from("events")
      .select("*, clients(first_name,last_name,email,phone)")
      .eq("id", id)
      .maybeSingle();
    ensure(error);
    return row<EventRecord>(data);
  },
  async save(input: {
    id?: string;
    client_id?: string | null;
    title: string;
    type: EventType;
    status: EventStatus;
    starts_at?: string | null;
    ends_at?: string | null;
    venue?: string | null;
    address?: string | null;
    attendees?: number | null;
    services?: string[];
    technical_requirements?: string | null;
  }): Promise<EventRecord> {
    const payload = {
      client_id: input.client_id || null,
      title: input.title.trim(),
      type: input.type,
      status: input.status,
      starts_at: input.starts_at || null,
      ends_at: input.ends_at || null,
      venue: input.venue || null,
      address: input.address || null,
      attendees: input.attendees || null,
      services: input.services ?? [],
      technical_requirements: input.technical_requirements || null,
    };
    const request = input.id
      ? supabase.from("events").update(payload).eq("id", input.id).select().single()
      : supabase.from("events").insert(payload).select().single();
    const { data, error } = await request;
    ensure(error);
    return row<EventRecord>(data) as EventRecord;
  },
  async updateStatus(id: string, status: EventStatus): Promise<void> {
    const { error } = await supabase.from("events").update({ status }).eq("id", id);
    ensure(error);
  },
  async setGuestProposalsOpen(id: string, open: boolean): Promise<void> {
    const { error } = await supabase
      .from("events")
      .update({ guest_proposals_open: open })
      .eq("id", id);
    ensure(error);
  },
  async portalEvents(): Promise<EventRecord[]> {
    return this.list();
  },
};

export const weddingRepository = {
  async sections(eventId: string): Promise<WeddingSection[]> {
    const { data, error } = await supabase
      .from("wedding_sections")
      .select("*")
      .eq("event_id", eventId)
      .order("section");
    ensure(error);
    return rows<WeddingSection>(data);
  },
  async saveSection(input: {
    eventId: string;
    section: WeddingSectionType;
    data: Record<string, string>;
    completed: boolean;
  }): Promise<void> {
    const { data: user } = await supabase.auth.getUser();
    const { error } = await supabase.from("wedding_sections").upsert(
      {
        event_id: input.eventId,
        section: input.section,
        data: input.data,
        completed: input.completed,
        updated_by: user.user?.id ?? null,
      },
      { onConflict: "event_id,section" },
    );
    ensure(error);
  },
  async deleteSection(eventId: string, section: WeddingSectionType): Promise<void> {
    const { error } = await supabase
      .from("wedding_sections")
      .delete()
      .eq("event_id", eventId)
      .eq("section", section);
    ensure(error);
  },
  async preferences(eventId: string): Promise<MusicPreference[]> {
    const { data, error } = await supabase
      .from("music_preferences")
      .select("*")
      .eq("event_id", eventId)
      .order("genre");
    ensure(error);
    return rows<MusicPreference>(data);
  },
  async savePreference(input: Omit<MusicPreference, "id"> & { id?: string }): Promise<void> {
    const payload = {
      event_id: input.event_id,
      genre: input.genre.trim(),
      percentage: input.percentage,
      preference: input.preference,
      forbidden: input.forbidden,
      comment: input.comment || null,
    };
    const request = input.id
      ? supabase.from("music_preferences").update(payload).eq("id", input.id)
      : supabase.from("music_preferences").insert(payload);
    const { error } = await request;
    ensure(error);
  },
  async deletePreference(id: string): Promise<void> {
    const { error } = await supabase.from("music_preferences").delete().eq("id", id);
    ensure(error);
  },
  async moments(eventId: string): Promise<SpecialMoment[]> {
    const { data, error } = await supabase
      .from("special_moments")
      .select("*")
      .eq("event_id", eventId)
      .order("sort_order");
    ensure(error);
    return rows<SpecialMoment>(data);
  },
  async saveMoment(
    input: Partial<SpecialMoment> & Pick<SpecialMoment, "event_id" | "name">,
  ): Promise<void> {
    const payload = {
      event_id: input.event_id,
      name: input.name,
      moment_type: input.moment_type ?? "OTHER",
      approximate_time: input.approximate_time || null,
      song_title: input.song_title || null,
      artist: input.artist || null,
      responsible_person: input.responsible_person || null,
      notes: input.notes || null,
      sort_order: input.sort_order ?? 0,
      confirmed: input.confirmed ?? false,
    };
    const request = input.id
      ? supabase.from("special_moments").update(payload).eq("id", input.id)
      : supabase.from("special_moments").insert(payload);
    const { error } = await request;
    ensure(error);
  },
  async submitPortal(eventId: string): Promise<void> {
    const { error } = await supabase.rpc("submit_portal_event", { target_event_id: eventId });
    ensure(error);
  },
};

export const songRepository = {
  async list(eventId?: string): Promise<Song[]> {
    let query = supabase
      .from("songs")
      .select("*")
      .is("archived_at", null)
      .order("priority", { ascending: false });
    if (eventId) query = query.eq("event_id", eventId);
    const { data, error } = await query;
    ensure(error);
    return rows<Song>(data);
  },
  async save(input: {
    id?: string;
    eventId: string;
    title: string;
    artist?: string;
    category: SongCategory;
    source: "ADMIN" | "CLIENT";
    comment?: string;
  }): Promise<void> {
    const { data: user } = await supabase.auth.getUser();
    const payload = {
      event_id: input.eventId,
      title: input.title.trim(),
      normalized_title: normalizeSongValue(input.title),
      artist: input.artist?.trim() ?? "",
      normalized_artist: normalizeSongValue(input.artist ?? ""),
      category: input.category,
      source: input.source,
      proposed_by: user.user?.id ?? null,
      comment: input.comment || null,
      moderation: "APPROVED" as ModerationStatus,
    };
    const request = input.id
      ? supabase.from("songs").update(payload).eq("id", input.id)
      : supabase.from("songs").insert(payload);
    const { error } = await request;
    ensure(error);
  },
  async moderate(id: string, moderation: ModerationStatus): Promise<void> {
    const { error } = await supabase.from("songs").update({ moderation }).eq("id", id);
    ensure(error);
  },
  async archive(id: string): Promise<void> {
    const { error } = await supabase
      .from("songs")
      .update({ archived_at: new Date().toISOString() })
      .eq("id", id);
    ensure(error);
  },
};

export const quoteRepository = {
  async list(eventId?: string): Promise<Quote[]> {
    let query = supabase
      .from("quotes")
      .select("*, events(title)")
      .order("created_at", { ascending: false });
    if (eventId) query = query.eq("event_id", eventId);
    const { data, error } = await query;
    ensure(error);
    return rows<Quote>(data);
  },
  async save(input: {
    id?: string;
    eventId: string;
    reference: string;
    totalCents: number;
    depositCents: number;
    validUntil?: string | null;
    notes?: string | null;
  }): Promise<void> {
    const payload = {
      event_id: input.eventId,
      reference: input.reference,
      total_cents: input.totalCents,
      deposit_cents: input.depositCents,
      valid_until: input.validUntil || null,
      notes: input.notes || null,
    };
    const request = input.id
      ? supabase.from("quotes").update(payload).eq("id", input.id).eq("status", "DRAFT")
      : supabase.from("quotes").insert(payload);
    const { error } = await request;
    ensure(error);
  },
  /**
   * Precio total de una boda: cambia el presupuesto aceptado o, si no hay
   * (montaje a medida), lo crea ya aceptado (`crm_fijar_precio`).
   */
  async fijarPrecio(eventId: string, totalCents: number): Promise<void> {
    const { error } = await supabase.rpc("crm_fijar_precio", {
      p_event_id: eventId,
      p_total_cents: totalCents,
    });
    ensure(error);
  },
  async setStatus(id: string, status: QuoteStatus): Promise<void> {
    const updates: Record<string, string | null> = { status };
    if (status === "SENT") updates.sent_at = new Date().toISOString();
    if (status === "ACCEPTED") updates.accepted_at = new Date().toISOString();
    const { error } = await supabase.from("quotes").update(updates).eq("id", id);
    ensure(error);
  },
};

export const paymentRepository = {
  async list(eventId?: string): Promise<Payment[]> {
    let query = supabase
      .from("payments")
      .select("*, events(title)")
      .order("created_at", { ascending: false });
    if (eventId) query = query.eq("event_id", eventId);
    const { data, error } = await query;
    ensure(error);
    return rows<Payment>(data);
  },
  async save(input: {
    eventId: string;
    quoteId?: string | null;
    concept: string;
    amountCents: number;
    paidAt?: string | null;
    method?: string | null;
    status: PaymentStatus;
    reference?: string | null;
  }): Promise<void> {
    const { error } = await supabase.from("payments").insert({
      event_id: input.eventId,
      quote_id: input.quoteId || null,
      concept: input.concept,
      amount_cents: input.amountCents,
      paid_at: input.paidAt || null,
      method: input.method || null,
      status: input.status,
      reference: input.reference || null,
    });
    ensure(error);
  },
  async markPaid(id: string, paidAt: string, method?: string | null): Promise<void> {
    const updates: Record<string, string> = { status: "PAID", paid_at: paidAt };
    if (method) updates.method = method;
    const { error } = await supabase.from("payments").update(updates).eq("id", id);
    ensure(error);
  },
};

export const taskRepository = {
  async list(eventId?: string): Promise<Task[]> {
    let query = supabase
      .from("tasks")
      .select("*, events(title)")
      .order("due_at", { ascending: true });
    if (eventId) query = query.eq("event_id", eventId);
    const { data, error } = await query;
    ensure(error);
    return rows<Task>(data);
  },
  async save(input: {
    eventId?: string | null;
    title: string;
    dueAt?: string | null;
    priority: number;
  }): Promise<void> {
    const { error } = await supabase.from("tasks").insert({
      event_id: input.eventId || null,
      title: input.title,
      due_at: input.dueAt || null,
      priority: input.priority,
    });
    ensure(error);
  },
  async setStatus(id: string, status: TaskStatus): Promise<void> {
    const { error } = await supabase
      .from("tasks")
      .update({ status, completed_at: status === "DONE" ? new Date().toISOString() : null })
      .eq("id", id);
    ensure(error);
  },
};

/** Notas internas de un cliente (tabla `notes`), después de reservar. */
export const noteRepository = {
  async ofClient(clientId: string): Promise<Note[]> {
    const { data, error } = await supabase
      .from("notes")
      .select("*")
      .eq("client_id", clientId)
      .order("created_at", { ascending: false });
    ensure(error);
    return rows<Note>(data);
  },
  async create(clientId: string, body: string, eventId?: string | null): Promise<void> {
    const { data: user } = await supabase.auth.getUser();
    const { error } = await supabase.from("notes").insert({
      client_id: clientId,
      event_id: eventId || null,
      body: body.trim(),
      created_by: user.user?.id ?? null,
    });
    ensure(error);
  },
};

const allowedContentTypes = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "image/jpeg",
  "image/png",
  "image/webp",
  "audio/mpeg",
  "audio/wav",
  "audio/x-m4a",
  "audio/mp4",
]);

export const fileRepository = {
  async list(eventId?: string): Promise<EventFile[]> {
    let query = supabase
      .from("files")
      .select("*, events(title)")
      .order("created_at", { ascending: false });
    if (eventId) query = query.eq("event_id", eventId);
    const { data, error } = await query;
    ensure(error);
    return rows<EventFile>(data);
  },
  async upload(
    eventId: string,
    file: File,
    category: FileCategory,
    visibleToClient: boolean,
  ): Promise<void> {
    if (!allowedContentTypes.has(file.type)) throw new Error("Tipo de archivo no permitido");
    const isAudio = file.type.startsWith("audio/");
    const maxBytes = isAudio ? 50 * 1024 * 1024 : 10 * 1024 * 1024;
    if (file.size > maxBytes)
      throw new Error(`El archivo supera el límite de ${isAudio ? 50 : 10} MB`);
    const extension = file.name.includes(".")
      ? file.name
          .split(".")
          .pop()
          ?.replace(/[^a-z0-9]/gi, "")
          .toLowerCase()
      : "bin";
    const objectKey = `${eventId}/${crypto.randomUUID()}.${extension || "bin"}`;
    const { error: uploadError } = await supabase.storage
      .from("event-files")
      .upload(objectKey, file, {
        contentType: file.type,
        upsert: false,
      });
    ensure(uploadError);
    const { data: user } = await supabase.auth.getUser();
    const { error } = await supabase.from("files").insert({
      event_id: eventId,
      bucket: "event-files",
      object_key: objectKey,
      original_filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
      category,
      visible_to_client: visibleToClient,
      uploaded_by: user.user?.id ?? null,
    });
    if (error) {
      await supabase.storage.from("event-files").remove([objectKey]);
      throw new Error(error.message);
    }
  },
  async signedUrl(file: EventFile): Promise<string> {
    const { data, error } = await supabase.storage
      .from(file.bucket)
      .createSignedUrl(file.object_key, 60);
    ensure(error);
    if (!data?.signedUrl) throw new Error("No se pudo generar el enlace");
    return data.signedUrl;
  },
  async remove(file: EventFile): Promise<void> {
    const { error: storageError } = await supabase.storage
      .from(file.bucket)
      .remove([file.object_key]);
    ensure(storageError);
    const { error } = await supabase.from("files").delete().eq("id", file.id);
    ensure(error);
  },
};

/**
 * Grabaciones de llamadas y reuniones en el bucket privado `call-recordings`
 * (solo admin). La transcripción y el análisis los guarda Claude desde la MCP
 * del CRM; aquí se sube el audio, se reproduce y se listan las pendientes.
 */
export const grabacionRepository = {
  /** Sube el audio a `{lead}/{interacción}.{ext}`, guarda la ruta y devuelve la interacción. */
  async adjuntar(input: {
    requestId: string;
    interactionId: string;
    file: File;
    durationS: number | null;
  }): Promise<LeadInteraction> {
    const { extension, contentType } = validarAudio(input.file);
    const path = rutaGrabacion(input.requestId, input.interactionId, extension);
    const { error: uploadError } = await supabase.storage
      .from(BUCKET_GRABACIONES)
      .upload(path, input.file, { contentType, upsert: true });
    ensure(uploadError);
    const { data, error } = await supabase.rpc("crm_guardar_grabacion", {
      p_interaction_id: input.interactionId,
      p_path: path,
      p_duration_s: input.durationS,
    });
    ensure(error);
    return row<LeadInteraction>(data) as LeadInteraction;
  },
  /** Enlace temporal para el reproductor: una hora, para poder pausar y volver. */
  async urlFirmada(path: string): Promise<string> {
    const { data, error } = await supabase.storage
      .from(BUCKET_GRABACIONES)
      .createSignedUrl(path, 3600);
    ensure(error);
    if (!data?.signedUrl) throw new Error("No se pudo abrir la grabación");
    return data.signedUrl;
  },
  /**
   * El audio entero por su enlace firmado. La política de seguridad de la web
   * solo deja reproducir `blob:` y el propio dominio, no Supabase directamente.
   */
  async audio(path: string): Promise<Blob> {
    const respuesta = await fetch(await this.urlFirmada(path));
    if (!respuesta.ok) throw new Error("No se pudo descargar la grabación");
    return respuesta.blob();
  },
  async pendientes(): Promise<LlamadaPendiente[]> {
    const { data, error } = await supabase.rpc("crm_llamadas_pendientes");
    ensure(error);
    return rows<LlamadaPendiente>(data);
  },
};

export const activityRepository = {
  async list(eventId?: string): Promise<ActivityItem[]> {
    let query = supabase
      .from("activity_log")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(50);
    if (eventId) query = query.eq("event_id", eventId);
    const { data, error } = await query;
    ensure(error);
    return rows<ActivityItem>(data);
  },
};

export const memberRepository = {
  async list(eventId: string) {
    const { data, error } = await supabase
      .from("event_members")
      .select("event_id,user_id,role,invited_email,invited_at,accepted_at")
      .eq("event_id", eventId);
    ensure(error);
    return rows<{
      event_id: string;
      user_id: string;
      role: "partner" | "client";
      invited_email: string;
      invited_at: string;
      accepted_at: string | null;
    }>(data);
  },
  async invite(eventId: string, email: string, displayName: string, role: "partner" | "client") {
    const { data } = await supabase.auth.getSession();
    const accessToken = data.session?.access_token;
    if (!accessToken) throw new Error("La sesión ha caducado");
    return inviteEventMember({
      data: {
        accessToken,
        eventId,
        email,
        displayName,
        role,
        // Con `?mode=reset` la persona invitada aterriza en la pantalla de elegir
        // contraseña. Sin él entraba directa al portal con la sesión del enlace y
        // se quedaba sin contraseña, así que no podía volver a entrar.
        redirectTo: `${window.location.origin}/acceso?mode=reset`,
      },
    });
  },
};

export const guestRepository = {
  sessionToken(): string {
    const key = "susiedad-guest-session";
    const existing = window.localStorage.getItem(key);
    if (existing) return existing;
    const token = `${crypto.randomUUID()}-${crypto.randomUUID()}`;
    window.localStorage.setItem(key, token);
    return token;
  },
  async event(linkToken: string, sessionToken: string): Promise<GuestEvent | null> {
    const { data, error } = await supabase.rpc("guest_event", {
      link_token: linkToken,
      session_token: sessionToken,
    });
    ensure(error);
    return rows<GuestEvent>(data)[0] ?? null;
  },
  async songs(linkToken: string, sessionToken: string): Promise<GuestSong[]> {
    const { data, error } = await supabase.rpc("guest_songs", {
      link_token: linkToken,
      session_token: sessionToken,
    });
    ensure(error);
    return rows<GuestSong>(data);
  },
  async propose(
    linkToken: string,
    sessionToken: string,
    alias: string,
    title: string,
    artist: string,
  ): Promise<void> {
    const { error } = await supabase.rpc("propose_guest_song", {
      link_token: linkToken,
      session_token: sessionToken,
      alias,
      song_title: title,
      song_artist: artist,
    });
    ensure(error);
  },
  async toggleVote(linkToken: string, sessionToken: string, songId: string): Promise<boolean> {
    const { data, error } = await supabase.rpc("toggle_guest_vote", {
      link_token: linkToken,
      session_token: sessionToken,
      target_song_id: songId,
    });
    ensure(error);
    return Boolean(data);
  },
};

export const guestLinkRepository = {
  async create(eventId: string, expiresAt?: string | null): Promise<string> {
    const { data, error } = await supabase.rpc("create_guest_link", {
      target_event_id: eventId,
      valid_until: expiresAt || null,
    });
    ensure(error);
    if (!data) throw new Error("No se pudo crear el enlace");
    return data as string;
  },
};

export const dashboardRepository = {
  async summary() {
    const today = new Date().toISOString();
    const [clients, events, contacts, upcoming, tasks, quotes, payments, activity] =
      await Promise.all([
        supabase
          .from("clients")
          .select("id", { count: "exact", head: true })
          .is("archived_at", null),
        supabase
          .from("events")
          .select("id", { count: "exact", head: true })
          .neq("status", "CANCELLED"),
        supabase
          .from("contact_requests")
          .select("id", { count: "exact", head: true })
          .eq("stage", "NUEVO"),
        supabase.from("events").select("*").gte("starts_at", today).order("starts_at").limit(5),
        supabase
          .from("tasks")
          .select("*,events(title)")
          .eq("status", "OPEN")
          .order("due_at")
          .limit(6),
        supabase.from("quotes").select("total_cents,deposit_cents,status"),
        supabase.from("payments").select("amount_cents,status"),
        supabase
          .from("activity_log")
          .select("*")
          .order("created_at", { ascending: false })
          .limit(6),
      ]);
    [clients, events, contacts, upcoming, tasks, quotes, payments, activity].forEach((result) =>
      ensure(result.error),
    );
    const quoteRows = rows<Pick<Quote, "total_cents" | "deposit_cents" | "status">>(quotes.data);
    const paymentRows = rows<Pick<Payment, "amount_cents" | "status">>(payments.data);
    const agreed = quoteRows
      .filter((quote) => quote.status === "ACCEPTED")
      .reduce((sum, quote) => sum + quote.total_cents, 0);
    const paid = paymentRows
      .filter((payment) => payment.status === "PAID")
      .reduce((sum, payment) => sum + payment.amount_cents, 0);
    return {
      clients: clients.count ?? 0,
      events: events.count ?? 0,
      contacts: contacts.count ?? 0,
      upcoming: rows<EventRecord>(upcoming.data),
      tasks: rows<Task>(tasks.data),
      activity: rows<ActivityItem>(activity.data),
      agreed,
      paid,
      pending: Math.max(agreed - paid, 0),
    };
  },
};

/** Campañas de Meta cruzadas con el CRM (`crm_campanas`) y su sincronización. */
export const campanasRepository = {
  async resumen(rango: RangoFechas): Promise<FilaCampana[]> {
    const { data, error } = await supabase.rpc("crm_campanas", {
      p_desde: rango.desde,
      p_hasta: rango.hasta,
    });
    ensure(error);
    return rows<Record<string, unknown>>(data).map(normalizarFilaCampana);
  },
  async ultimaSincronizacion(): Promise<UltimaSincronizacion | null> {
    const { data, error } = await supabase
      .from("meta_sync_state")
      .select("value")
      .eq("key", "sync")
      .maybeSingle();
    ensure(error);
    return (row<{ value: UltimaSincronizacion }>(data)?.value ??
      null) as UltimaSincronizacion | null;
  },
  async sincronizarAhora(): Promise<ResumenSincronizacion> {
    const { data } = await supabase.auth.getSession();
    const accessToken = data.session?.access_token;
    if (!accessToken) throw new Error("La sesión ha caducado");
    return sincronizarMetaAhora({ data: { accessToken } });
  },
};

/** Propuestas por enlace: una por lead, con sus visitas. */
export const propuestaRepository = {
  async delLead(leadId: string): Promise<LeadProposal | null> {
    const { data, error } = await supabase
      .from("lead_proposals")
      .select("*")
      .eq("request_id", leadId)
      .is("revoked_at", null)
      .maybeSingle();
    ensure(error);
    return row<LeadProposal>(data);
  },
  /** Idempotente: si ya hay enlace, devuelve el mismo. */
  async crear(leadId: string): Promise<LeadProposal> {
    const { data, error } = await supabase.rpc("crm_crear_propuesta", { p_request_id: leadId });
    ensure(error);
    return row<LeadProposal>(data) as LeadProposal;
  },
  async eventos(proposalId: string): Promise<LeadProposalEvent[]> {
    const { data, error } = await supabase
      .from("lead_proposal_events")
      .select("*")
      .eq("proposal_id", proposalId)
      .order("created_at", { ascending: false })
      .limit(50);
    ensure(error);
    return rows<LeadProposalEvent>(data);
  },
  /** Sube la imagen de la vista previa de WhatsApp y la deja apuntada en la propuesta. */
  async guardarVistaPrevia(propuesta: LeadProposal, imagen: Blob): Promise<void> {
    const ruta = `${propuesta.token}/vista-previa.jpg`;
    const { error: subida } = await supabase.storage
      .from("propuestas")
      .upload(ruta, imagen, { contentType: "image/jpeg", upsert: true });
    ensure(subida);
    const { error } = await supabase
      .from("lead_proposals")
      .update({ og_image_path: ruta })
      .eq("id", propuesta.id);
    ensure(error);
  },
};

/** Suscripciones a los avisos push de este navegador. */
export const avisosRepository = {
  async guardar(suscripcion: PushSubscriptionJSON): Promise<void> {
    const { endpoint, keys } = suscripcion;
    if (!endpoint || !keys?.p256dh || !keys.auth) throw new Error("Suscripción push incompleta");
    const { error } = await supabase
      .from("push_subscriptions")
      .upsert(
        { endpoint, p256dh: keys.p256dh, auth: keys.auth, user_agent: navigator.userAgent },
        { onConflict: "endpoint" },
      );
    ensure(error);
  },
  async borrar(endpoint: string): Promise<void> {
    const { error } = await supabase.from("push_subscriptions").delete().eq("endpoint", endpoint);
    ensure(error);
  },
};

async function tokenDeSesion(): Promise<string> {
  const { data } = await supabase.auth.getSession();
  const accessToken = data.session?.access_token;
  if (!accessToken) throw new Error("La sesión ha caducado");
  return accessToken;
}

/** Lo que devuelve `crm_whatsapp_importar`. */
export interface ResultadoImportacion {
  nuevos: number;
  ya_importados: number;
  ya_en_vivo: number;
}

const COLUMNAS_WHATSAPP =
  "id, wa_message_id, request_id, telefono, direccion, origen, tipo, texto, media_path, media_mime, enviado_at";

/** WhatsApp: la conversación de cada lead y la conexión con Meta. */
export const whatsappRepository = {
  /** Los últimos mensajes del lead, del más antiguo al más reciente. */
  async conversacion(leadId: string, limite = 200): Promise<WhatsappMensaje[]> {
    const { data, error } = await supabase
      .from("whatsapp_mensajes")
      .select(COLUMNAS_WHATSAPP)
      .eq("request_id", leadId)
      .order("enviado_at", { ascending: false })
      .limit(limite);
    ensure(error);
    return rows<WhatsappMensaje>(data).reverse();
  },
  async urlArchivo(path: string): Promise<string> {
    const { data, error } = await supabase.storage
      .from("whatsapp-media")
      .createSignedUrl(path, 600);
    ensure(error);
    if (!data?.signedUrl) throw new Error("No se pudo abrir el archivo");
    return data.signedUrl;
  },
  /** Como las grabaciones: la política de seguridad solo deja reproducir `blob:`. */
  async archivo(path: string): Promise<Blob> {
    const respuesta = await fetch(await this.urlArchivo(path));
    if (!respuesta.ok) throw new Error("No se pudo descargar el archivo");
    return respuesta.blob();
  },
  /**
   * Guarda en el lead un chat exportado a mano. Se puede repetir: solo entra lo
   * que no estaba (ni importado antes ni llegado por el webhook).
   */
  async importarExportado(
    leadId: string,
    mensajes: MensajeAImportar[],
  ): Promise<ResultadoImportacion> {
    const total: ResultadoImportacion = { nuevos: 0, ya_importados: 0, ya_en_vivo: 0 };
    for (const tanda of enTandas(mensajes)) {
      const { data, error } = await supabase.rpc("crm_whatsapp_importar", {
        p_request_id: leadId,
        p_mensajes: tanda,
      });
      ensure(error);
      const fila = rows<ResultadoImportacion>(data)[0];
      total.nuevos += fila?.nuevos ?? 0;
      total.ya_importados += fila?.ya_importados ?? 0;
      total.ya_en_vivo += fila?.ya_en_vivo ?? 0;
    }
    return total;
  },
  /** El lead de un teléfono, con el mismo criterio que el webhook. */
  async leadDeTelefono(telefono: string): Promise<string | null> {
    const { data, error } = await supabase.rpc("crm_whatsapp_lead_de_telefono", {
      p_telefono: telefono,
    });
    ensure(error);
    return (data as string | null) ?? null;
  },
  async estado(): Promise<WhatsappEstado> {
    const { data, error } = await supabase.rpc("crm_whatsapp_estado");
    ensure(error);
    return (row<WhatsappEstado>(data) ?? { conectado: false }) as WhatsappEstado;
  },
  async configuracion(): Promise<ConfiguracionWhatsapp> {
    return configuracionWhatsapp({ data: { accessToken: await tokenDeSesion() } });
  },
  async conectar(datos: {
    codigo: string;
    wabaId: string | null;
    phoneNumberId: string | null;
  }): Promise<ResultadoConexion> {
    return conectarWhatsappFn({ data: { accessToken: await tokenDeSesion(), ...datos } });
  },
  async reintentar(): Promise<ResultadoConexion> {
    return reintentarConexionWhatsapp({ data: { accessToken: await tokenDeSesion() } });
  },
};
